import { supabase } from "@/lib/supabase";
import { parseRpcOrFallback } from "@/lib/parse-rpc";
import {
  dmcaSummarySchema,
  dmcaSettingsSchema,
  DEFAULT_DMCA_SETTINGS,
  EMPTY_DMCA_SUMMARY,
  type DmcaSummary,
  type DmcaSettings,
  type DmcaTargetRow,
  type DmcaCheckRow,
  type DmcaKind,
  type DmcaOverrideRow,
  type TargetSearchRow,
} from "@/types/dmca";

export const DMCA_PAGE_SIZE = 50;

// Columns selected for a target row — kept in one place so the grid, the URL
// children and the type stay in step.
const TARGET_COLS =
  "id,kind,target,domain,tier,brand,geo,clicks,last_status,last_kind,last_notices,last_removed,last_notice_ids,url_present,last_checked_at,flagged_since,error_streak";

// The dmca_* tables / get_dmca_summary are not in the generated Database types
// yet. Structural escape hatches (same spirit as looseRpc in ClicksService):
// no `any`, no @ts-expect-error to rot — the response is validated/cast at the
// call site. The real auth/RLS runs server-side on the authenticated client.
interface LooseResult {
  data: unknown;
  error: { message: string } | null;
  count?: number | null;
}
interface LooseQuery extends Promise<LooseResult> {
  select: (cols: string, opts?: { count?: "exact" }) => LooseQuery;
  insert: (values: Record<string, unknown> | Record<string, unknown>[]) => LooseQuery;
  update: (values: Record<string, unknown>) => LooseQuery;
  delete: () => LooseQuery;
  eq: (col: string, val: unknown) => LooseQuery;
  is: (col: string, val: unknown) => LooseQuery;
  ilike: (col: string, val: string) => LooseQuery;
  order: (col: string, opts?: { ascending?: boolean }) => LooseQuery;
  range: (from: number, to: number) => LooseQuery;
  limit: (n: number) => LooseQuery;
}

const looseFrom = (table: string): LooseQuery =>
  (supabase.from as unknown as (t: string) => LooseQuery)(table);

const looseRpc = (fn: string, args: Record<string, unknown> = {}): Promise<LooseResult> =>
  (supabase.rpc as unknown as (fn: string, args: Record<string, unknown>) => Promise<LooseResult>)(
    fn,
    args,
  );

export type DmcaStatusFilter = "all" | "dmca" | "clean" | "unchecked";
export type DmcaKindFilter = "all" | DmcaKind;
export type DmcaTierFilter = "all" | 1 | 2 | 3;

export type DmcaSortCol =
  | "target"
  | "last_status"
  | "tier"
  | "clicks"
  | "last_notices"
  | "flagged_since"
  | "last_checked_at";

// Whitelist — the sort column goes straight into .order(), so it must never be
// user-controlled free text.
const SORTABLE: ReadonlySet<string> = new Set<DmcaSortCol>([
  "target",
  "last_status",
  "tier",
  "clicks",
  "last_notices",
  "flagged_since",
  "last_checked_at",
]);

export interface DmcaTargetsParams {
  kind: DmcaKindFilter;
  status: DmcaStatusFilter;
  tier: DmcaTierFilter;
  brand: string | null;
  geo: string | null;
  search: string;
  sort?: { col: DmcaSortCol; dir: "asc" | "desc" };
  page: number; // 1-based
  pageSize?: number;
}

class DmcaService {
  private static instance: DmcaService;
  private constructor() {}
  public static getInstance(): DmcaService {
    if (!DmcaService.instance) DmcaService.instance = new DmcaService();
    return DmcaService.instance;
  }

  /** KPI + health numbers (get_dmca_summary). SECURITY DEFINER, granted to authenticated. */
  public async getSummary(): Promise<DmcaSummary> {
    const { data, error } = await looseRpc("get_dmca_summary");
    if (error) throw new Error(error.message);
    return parseRpcOrFallback(dmcaSummarySchema, data, EMPTY_DMCA_SUMMARY, "get_dmca_summary");
  }

  /** One server-side page of targets. dmca_targets is a plain indexed table, so
   *  filter + paginate on the server (not fetch-all, unlike the Clicks grid). */
  public async getTargets(
    p: DmcaTargetsParams,
  ): Promise<{ rows: DmcaTargetRow[]; total: number }> {
    const pageSize = p.pageSize ?? DMCA_PAGE_SIZE;
    const from = (p.page - 1) * pageSize;
    const to = from + pageSize - 1;

    let q = looseFrom("dmca_targets")
      .select(TARGET_COLS, { count: "exact" })
      .eq("active", true);

    if (p.kind !== "all") q = q.eq("kind", p.kind);
    if (p.tier !== "all") q = q.eq("tier", p.tier);
    if (p.status === "dmca") q = q.eq("last_status", "dmca");
    else if (p.status === "clean") q = q.eq("last_status", "clean");
    else if (p.status === "unchecked") q = q.is("last_status", null);
    if (p.brand) q = q.eq("brand", p.brand);
    if (p.geo) q = q.eq("geo", p.geo);
    if (p.search) q = q.ilike("target", `%${p.search}%`);

    // User sort if given (whitelisted), else tier 1 first then biggest earners.
    if (p.sort && SORTABLE.has(p.sort.col)) {
      q = q.order(p.sort.col, { ascending: p.sort.dir === "asc" });
    } else {
      q = q.order("tier", { ascending: true }).order("clicks", { ascending: false });
    }
    const { data, error, count } = await q.range(from, to);
    if (error) throw new Error(error.message);
    return { rows: (data as DmcaTargetRow[]) ?? [], total: count ?? 0 };
  }

  /** Check history for one target (newest first). */
  public async getChecks(targetId: number): Promise<DmcaCheckRow[]> {
    const { data, error } = await looseFrom("dmca_checks")
      .select("id,checked_at,status,notices,removed,own_urls,url_present,error")
      .eq("target_id", targetId)
      .order("checked_at", { ascending: false })
      .limit(50);
    if (error) throw new Error(error.message);
    return (data as DmcaCheckRow[]) ?? [];
  }

  /** The URL targets under a domain (affected first when asked). */
  public async getUrlChildren(
    domain: string,
    affectedOnly = false,
  ): Promise<DmcaTargetRow[]> {
    let q = looseFrom("dmca_targets")
      .select(TARGET_COLS)
      .eq("kind", "url")
      .eq("domain", domain)
      .eq("active", true);
    if (affectedOnly) q = q.eq("last_status", "dmca");
    const { data, error } = await q.order("clicks", { ascending: false }).limit(200);
    if (error) throw new Error(error.message);
    return (data as DmcaTargetRow[]) ?? [];
  }

  /** Tiering + schedule settings (the single dmca_settings row). */
  public async getSettings(): Promise<DmcaSettings> {
    const { data, error } = await looseFrom("dmca_settings")
      .select("tier1_limit,clicks_window_days,tier1_interval_hours,tier2_interval_hours,tier3_interval_hours,tier_metric")
      .eq("id", 1)
      .limit(1);
    if (error) throw new Error(error.message);
    const row = Array.isArray(data) ? data[0] : data;
    return parseRpcOrFallback(dmcaSettingsSchema, row, DEFAULT_DMCA_SETTINGS, "dmca_settings");
  }

  /** Admin-only update (RLS enforces it). Returns the number of rows changed —
   *  0 means the caller isn't admin (RLS silently updates nothing, no error). */
  public async updateSettings(patch: Partial<DmcaSettings>): Promise<number> {
    const { data, error } = await looseFrom("dmca_settings")
      .update(patch)
      .eq("id", 1)
      .select("tier1_limit");
    if (error) throw new Error(error.message);
    return Array.isArray(data) ? data.length : 0;
  }

  // ---- VIP overrides (dmca_overrides) ----------------------------------------
  // Pin a target to its own re-check interval on top of its tier. Reads are open
  // to any signed-in user; writes are admin-only (RLS). The checker honours an
  // active override regardless of tier/active (migration 20261021100000).

  /** Active VIP overrides with their target, most recent first. */
  public async getOverrides(): Promise<DmcaOverrideRow[]> {
    const { data, error } = await looseFrom("dmca_overrides")
      .select(
        "id,target_id,interval_hours,reason,active,created_at,dmca_targets(target,kind,tier,domain,brand)",
      )
      .eq("active", true)
      .order("created_at", { ascending: false });
    if (error) throw new Error(error.message);
    const rows = Array.isArray(data) ? data : [];
    // Flatten the embedded target row onto the override.
    return rows.map((r) => {
      const o = r as Record<string, unknown>;
      const t = (o.dmca_targets ?? {}) as Record<string, unknown>;
      return {
        id: Number(o.id),
        target_id: Number(o.target_id),
        interval_hours: Number(o.interval_hours),
        reason: (o.reason as string) ?? null,
        active: Boolean(o.active),
        created_at: String(o.created_at),
        target: String(t.target ?? ""),
        kind: (t.kind as DmcaKind) ?? "domain",
        tier: (Number(t.tier) || 1) as 1 | 2 | 3,
        domain: String(t.domain ?? ""),
        brand: (t.brand as string) ?? null,
      };
    });
  }

  /** Targets matching a search, for the add box. Any tier (a VIP pin can force
   *  an untracked/tier-3 target back into checking). Returns up to 10. */
  public async searchTargets(query: string): Promise<TargetSearchRow[]> {
    const q = query.trim();
    if (!q) return [];
    const { data, error } = await looseFrom("dmca_targets")
      .select("id,target,kind,tier,domain")
      .ilike("target", `%${q}%`)
      .order("tier", { ascending: true })
      .limit(10);
    if (error) throw new Error(error.message);
    return (Array.isArray(data) ? data : []) as TargetSearchRow[];
  }

  /** Pin a target (admin-only via RLS). Returns the new rows (0 = not admin).
   *  unique(target_id) means re-pinning the same target would 409 — callers
   *  should avoid offering an already-pinned target. */
  public async addOverride(
    targetId: number,
    intervalHours: number,
    reason: string | null,
  ): Promise<number> {
    const { data, error } = await looseFrom("dmca_overrides")
      .insert({ target_id: targetId, interval_hours: intervalHours, reason })
      .select("id");
    if (error) throw new Error(error.message);
    return Array.isArray(data) ? data.length : 0;
  }

  /** Change a pin's interval (admin-only). Returns rows changed (0 = not admin). */
  public async updateOverrideInterval(id: number, intervalHours: number): Promise<number> {
    const { data, error } = await looseFrom("dmca_overrides")
      .update({ interval_hours: intervalHours })
      .eq("id", id)
      .select("id");
    if (error) throw new Error(error.message);
    return Array.isArray(data) ? data.length : 0;
  }

  /** Remove a pin (admin-only). Returns rows deleted (0 = not admin). */
  public async removeOverride(id: number): Promise<number> {
    const { data, error } = await looseFrom("dmca_overrides")
      .delete()
      .eq("id", id)
      .select("id");
    if (error) throw new Error(error.message);
    return Array.isArray(data) ? data.length : 0;
  }
}

export const dmcaService = DmcaService.getInstance();
